import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Transition } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import { useWindowSize, isElectron } from "./Globals";
import { ROUTES } from "../lib/appRoutes";
import { apiUrl } from "../lib/apiBase";
import { apiCredits } from "../lib/apiRoutes";
import http from "../lib/httpClient";

import Logo from "../assets/e_logo.svg";
import { onAuthStateChanged, User } from "firebase/auth";
import { webAuth, mobileAuth as mobileAuthInstance } from "../lib/firebaseApp";
import { isNativeWebView, getShell } from "../lib/apiKey";

export default function Header() {
  /*
    This is a component function in JSX that contains the HTML markup that represent each graphical element on the webpage

    Parameters
    ----------
    None

    Returns
    -------
    DOM File
        A HTML markup that contains graphical elements
  */
  const size = useWindowSize();

  const location = useLocation();
  const { i18n } = useTranslation();

  // Atomizer family shells (iOS/iPad AR, Mac, Vision Pro), EV-Mac, and
  // Electronify all have native auth + membership surfaces, so the web
  // header's right-side nav (Sign in / Member / Spotlight / Articles /
  // hamburger) is suppressed in every native shell to avoid duplicates.
  const nativeShellWithChrome = (() => {
    const s = getShell();
    return s === "atomizer-ar"
      || s === "atomizer-mac"
      || s === "atomizer-vision"
      || s === "electronvisualized-mac"
      || s === "electronify";
  })();

  // Subset that ALSO renders a native top-right credits chip — those
  // shells suppress the web's inline credits pill to avoid double
  // counters. Electronify is intentionally NOT in this set: its
  // WebView wrapper has no native top bar, so the pill is the only
  // way the user sees their remaining credits while browsing.
  const nativeShellHidesCreditPill = (() => {
    const s = getShell();
    return s === "atomizer-ar"
      || s === "atomizer-mac"
      || s === "atomizer-vision"
      || s === "electronvisualized-mac";
  })();

  const [showMenu, setMenu] = useState(false);
  const [showMenuAlreadyTriggered, setMenuAlreadyTriggered] = useState(false);
  const [logged, setLogged] = useState(false);
  const [creditsBadge, setCreditsBadge] = useState<string | null>(null);
  const [devMode, setDevMode] = useState(() => {
    return typeof window !== "undefined" && window.localStorage.getItem("devMode") === "true";
  });
  const isDevUrl = typeof window !== "undefined" && window.location.hostname.includes("-dev");

  // Expose devMode globally so credits system can check it
  useEffect(() => {
    (window as any).__EV_DEV_MODE = devMode;
    window.localStorage.setItem("devMode", devMode ? "true" : "false");
  }, [devMode]);

  const fetchCredits = (uid?: string, realm?: "mobile" | "web") => {
    http.get(apiUrl(apiCredits(uid, realm)))
      .then((r) => {
        const { remaining, total } = r.data;
        setCreditsBadge(total === -1 ? "\u221e" : `${remaining}/${total}`);
      })
      .catch(() => {});
  };

  // Live credit updates — httpClient dispatches this when any
  // credit-protected endpoint returns its updated count via headers.
  useEffect(() => {
    const onUpdate = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (!detail) return;
      const { remaining, total } = detail as { remaining: number; total: number };
      if (Number.isFinite(remaining) && Number.isFinite(total)) {
        setCreditsBadge(total === -1 ? "\u221e" : `${remaining}/${total}`);
      }
    };
    window.addEventListener("ev-credits-update", onUpdate);
    return () => window.removeEventListener("ev-credits-update", onUpdate);
  }, []);

  useEffect(() => {
    // Watch both web realm (electronvisualized) and mobile realm
    // (atomizer-ar). Either one signed in flips the header to the
    // Member state.
    let webUser: User | null = null;
    let mobileUser: User | null = null;

    const resolve = () => {
      // Strict per-realm: web user (if any) wins because Stripe-backed
      // entitlements live in the ElectronVisualized RC project; only fall
      // back to mobile realm when no web sign-in exists. The picked realm
      // tells the server which RC project to query for credits.
      if (webUser) {
        setLogged(true);
        fetchCredits(webUser.uid, "web");
      } else if (mobileUser) {
        setLogged(true);
        fetchCredits(mobileUser.uid, "mobile");
      } else {
        setLogged(false);
        setCreditsBadge(null);
        fetchCredits();
      }
    };

    const unsubWeb = onAuthStateChanged(webAuth, (u) => {
      webUser = u;
      resolve();
    });
    const unsubMobile = onAuthStateChanged(mobileAuthInstance, (u) => {
      mobileUser = u;
      resolve();
    });

    // Refresh credits every 30s
    const interval = setInterval(() => {
      const webUid = webAuth.currentUser?.uid;
      const mobileUid = mobileAuthInstance.currentUser?.uid;
      if (webUid) fetchCredits(webUid, "web");
      else if (mobileUid) fetchCredits(mobileUid, "mobile");
      else fetchCredits();
    }, 30000);

    return () => {
      clearInterval(interval);
      unsubWeb();
      unsubMobile();
    };
  }, []);

  const navigate = useNavigate();

  const movePage = (page) => {
    /*
    This function, when executed, navigates the user to another page smoothly without a flicker

    Parameters
    ----------
    page: String
      The desired URL

    Returns
    -------
    None
    */
    setMenu(false);
    navigate(page);
  };

  // Responsive Menu Design
  useEffect(() => {
    if (size.width > 1024) {
      setMenu(true);
      setMenuAlreadyTriggered(true);
    } else if (showMenuAlreadyTriggered) {
      setMenu(false);
      setMenuAlreadyTriggered(false);
    }
  }, [showMenu, showMenuAlreadyTriggered, size.width]);

  return (
    <nav
      className="sticky top-0 z-40 flex flex-wrap items-center justify-between gap-2 border-b border-white/5 bg-zinc-950/85 px-4 py-3 backdrop-blur-md"
      style={{ paddingTop: "max(0.75rem, env(safe-area-inset-top))" }}
    >
      <button
        type="button"
        className="flex items-center gap-3 text-left rounded-md outline-none focus-visible:ring-2 focus-visible:ring-zinc-500"
        onClick={() => {
          movePage(ROUTES.home);
        }}
      >
        <img
          className="h-9 w-9 shrink-0 opacity-90"
          src={Logo}
          alt=""
          style={
            // Purple-tinted Atomizer logo across the Atomizer family
            // — iOS AR shell, Mac shell, Vision Pro shell. Other
            // shells (electronify, electronvisualized-mac, plain web)
            // keep the default pink e-logo.
            (() => {
              const s = getShell();
              return (s === "atomizer-ar" || s === "atomizer-mac" || s === "atomizer-vision")
                ? { filter: "hue-rotate(280deg) saturate(1.5) brightness(1.05)" }
                : undefined;
            })()
          }
        />
        <span className="text-base font-medium tracking-tight text-zinc-100">
          {(() => {
            const s = getShell();
            if (s === "atomizer-ar" || s === "atomizer-mac" || s === "atomizer-vision") return "Atomizer AR";
            if (s === "electronify") return "Electronify";
            // electronvisualized-mac + plain web → default brand.
            return "ElectronVisualized";
          })()}
        </span>
        {/* WEB badge — visible only when no native shell tagged the
            URL with ?shell=… (i.e., user opened electronvisual.org in
            a regular browser, not from an iOS / Mac / Vision / EV-Mac
            / Electronify wrapper). Pink outline matches the default
            pink e-logo on plain web. */}
        {getShell() === null && (
          <span className="ml-1 rounded-md border border-pink-500 px-1.5 py-0.5 text-[10px] font-semibold tracking-wide text-pink-400">
            WEB
          </span>
        )}
      </button>

      {/* Plain web shows an inline credits pill below near the language
          selector. Every native shell (atomizer-*, ev-mac, electronify)
          renders its own native chip and suppresses the web one. */}
      {devMode && (
        <span className="inline-flex items-center rounded-full bg-yellow-500/20 border border-yellow-500/40 px-2.5 py-0.5 text-xs text-yellow-400">
          DEV MODE
        </span>
      )}
      {isDevUrl && (
        <button
          onClick={() => setDevMode(!devMode)}
          className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] transition-colors ${
            devMode
              ? "bg-yellow-500/20 border border-yellow-500/40 text-yellow-400"
              : "bg-zinc-800 border border-zinc-700 text-zinc-500 hover:text-zinc-300"
          }`}
        >
          {devMode ? "DEV" : "dev"}
        </button>
      )}
      {devMode && (
        <button
          onClick={async () => {
            try {
              // Clear browser caches
              if ("caches" in window) {
                const keys = await caches.keys();
                await Promise.all(keys.map((k) => caches.delete(k)));
              }
              // Clear localStorage cache entries
              for (let i = localStorage.length - 1; i >= 0; i--) {
                const key = localStorage.key(i);
                if (key && (key.startsWith("mo_") || key.startsWith("density_"))) {
                  localStorage.removeItem(key);
                }
              }
              alert("Cache cleared! Reload the page.");
            } catch {
              alert("Cache clear failed.");
            }
          }}
          className="inline-flex items-center rounded-full bg-red-500/20 border border-red-500/40 px-2 py-0.5 text-[10px] text-red-400 hover:bg-red-500/30 transition-colors"
        >
          Clear Cache
        </button>
      )}

      {/* Inline credits pill — sits next to the language selector on
          plain web AND inside Electronify's WebView (which has no
          native credit chip of its own). Atomizer + EV-Mac suppress
          this because their native top bar already shows credits.
          Click → /credits page (same as the floating). */}
      {creditsBadge && !devMode && !isElectron() && !nativeShellHidesCreditPill && (
        <button
          type="button"
          onClick={() => movePage(ROUTES.credits)}
          title="See plans and credits"
          className="inline-flex items-center gap-1.5 rounded-full border border-zinc-700 bg-zinc-800 px-2 py-0.5 text-[10px] text-zinc-100 hover:bg-zinc-700 transition-colors"
        >
          <span className="text-yellow-400">{creditsBadge.includes("∞") ? "∞" : "⚡"}</span>
          <span className="font-medium tabular-nums">
            {creditsBadge.includes("∞") ? "Credits" : creditsBadge}
          </span>
        </button>
      )}

      {/* Language selector */}
      <select
        value={i18n.language?.split("-")[0] || "en"}
        onChange={(e) => i18n.changeLanguage(e.target.value)}
        className="bg-zinc-800 border border-zinc-700 text-zinc-400 text-[10px] rounded px-1.5 py-0.5 outline-none"
      >
        <option value="en">EN</option>
        <option value="ko">한국어</option>
        <option value="ja">日本語</option>
        <option value="zh-CN">简体中文</option>
        <option value="zh-TW">繁體中文</option>
        <option value="es">Español</option>
        <option value="fr">Français</option>
        <option value="de">Deutsch</option>
        <option value="pt">Português</option>
        <option value="ru">Русский</option>
        <option value="ar">العربية</option>
        <option value="hi">हिन्दी</option>
        <option value="it">Italiano</option>
        <option value="nl">Nederlands</option>
        <option value="pl">Polski</option>
        <option value="sv">Svenska</option>
        <option value="tr">Türkçe</option>
        <option value="vi">Tiếng Việt</option>
        <option value="th">ไทย</option>
        <option value="id">Indonesia</option>
      </select>

      {isElectron() && (
        <button
          type="button"
          onClick={() => {
            movePage(ROUTES.download);
          }}
          className="order-last ml-auto text-sm text-zinc-400 hover:text-zinc-100 lg:order-none"
        >
          iOS app
        </button>
      )}

      <div className="flex flex-row mt-4 sm:mt-0 lg:hidden">
        {!isElectron() && !isNativeWebView() && !nativeShellWithChrome && (
          <>
            {!logged ? (
              <button
                onClick={() => {
                  movePage(ROUTES.login);
                }}
                className={`flex items-center rounded-md border border-zinc-700 bg-zinc-900/50 px-3 py-1.5 text-sm text-zinc-200 hover:border-zinc-500 hover:bg-zinc-800 ${
                  size.width < 380 ? "mt-5 mr-2" : "mr-2"
                }`}
              >
                <span>Sign in</span>
              </button>
            ) : (
              <button
                onClick={() => {
                  movePage(ROUTES.membership);
                }}
                className={`flex items-center rounded-md border border-zinc-700 bg-zinc-900/50 px-3 py-1.5 text-sm text-zinc-200 hover:border-zinc-500 hover:bg-zinc-800 ${
                  size.width < 380 ? "mt-5 mr-2" : "mr-2"
                }`}
              >
                <span>Member</span>
              </button>
            )}
          </>
        )}

        {!isElectron() && !nativeShellWithChrome && (
          <button
            onClick={() => {
              setMenu(!showMenu);
            }}
            className={`flex items-center rounded-md border border-zinc-700 bg-zinc-900/50 px-3 py-1.5 text-sm text-zinc-200 hover:border-zinc-500 hover:bg-zinc-800 ${
              size.width < 380 ? "mt-5 mr-5" : "mr-5"
            }`}
          >
            {size.width < 380 && <span className="mr-2">Menu</span>}

            <svg
              className="h-3 w-3 fill-current text-zinc-400"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <title>Menu</title>

              <path d="M0 3h20v2H0V3zm0 6h20v2H0V9zm0 6h20v2H0v-2z" />
            </svg>
          </button>
        )}
      </div>
      <div className="w-full block flex lg:items-center lg:w-auto">
        <Transition
          show={showMenu}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-200"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <div className="text-sm lg:flex-grow">
            {!isElectron() && !nativeShellWithChrome && (
              <>
                {!logged ? (
                  <button
                    onClick={() => {
                      movePage(ROUTES.login);
                    }}
                    className={`hidden items-center rounded-md border border-zinc-700 bg-zinc-900/50 px-3 py-1.5 text-sm text-zinc-200 hover:border-zinc-500 hover:bg-zinc-800 lg:inline-flex ${
                      size.width < 380 ? "mt-5 mr-5" : "mr-5"
                    }`}
                  >
                    <span>Sign in</span>
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      movePage(ROUTES.membership);
                    }}
                    className={`hidden items-center rounded-md border border-zinc-700 bg-zinc-900/50 px-3 py-1.5 text-sm text-zinc-200 hover:border-zinc-500 hover:bg-zinc-800 lg:inline-flex ${
                      size.width < 380 ? "mt-5 mr-5" : "mr-5"
                    }`}
                  >
                    <span>Member</span>
                  </button>
                )}
              </>
            )}

            {!isElectron() && !nativeShellWithChrome && (
              <>
                <button
                  onClick={() => {
                    movePage(ROUTES.renderer);
                  }}
                  className={`mt-4 block text-sm lg:mr-4 lg:mt-0 lg:inline-block ${
                    location.pathname === ROUTES.renderer
                      ? "text-zinc-500"
                      : "text-zinc-400 hover:text-zinc-100"
                  }`}
                >
                  <span>Spotlight</span>
                </button>
                {/* {/* 
            <button
              onClick={() => {
                movePage(ROUTES.dev);
              }}
              className={`block mt-4 lg:inline-block lg:mt-0 ${
                location.pathname === ROUTES.dev ? "text-gray-400" : "text-rose-200"
              } hover:text-white mr-4`}
            >
              <span>API</span>
            </button> */}

            <button
              onClick={() => {
                movePage(ROUTES.blog);
              }}
              className={`mt-4 block text-sm lg:mt-0 lg:inline-block ${
                location.pathname === ROUTES.blog
                  ? "text-zinc-500"
                  : "text-zinc-400 hover:text-zinc-100"
              }`}
            >
              <span>Articles</span>
            </button>
              </>
            )}
          </div>
        </Transition>
      </div>
    </nav>
  );
}
